import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { ApplicationSettingToSave } from "../../models";

import ApplicationSettingsApi from "../../api/applicationSettingsApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { AppSettingSaveStatus } from "../../models";

/** Every setting definition, loaded once and kept for the session */
export function useDefinitionsQuery() {
	return useQuery({
		queryKey: [QUERY_KEYS.AppSettings.Definitions],
		queryFn: () => ApplicationSettingsApi.getDefinitions(),
	});
}

/** Every setting row, loaded once and kept until a save or delete invalidates it */
export function useSettingsQuery() {
	return useQuery({
		queryKey: [QUERY_KEYS.AppSettings.Settings],
		queryFn: () => ApplicationSettingsApi.getAllSettings(),
	});
}

/** Saves a setting, and reloads the setting rows when it saved */
export function useSaveSettingMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ setting, overwrite }: { setting: ApplicationSettingToSave; overwrite?: boolean }) =>
			ApplicationSettingsApi.saveSetting(setting, overwrite),
		// Awaited, so the dialog closes once the grid has the saved row
		onSuccess: async (result) => {
			if (result?.status === AppSettingSaveStatus.Saved)
				await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AppSettings.Settings] });
		},
	});
}

/** Deletes a setting row, and reloads the setting rows when it was deleted */
export function useDeleteSettingMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => ApplicationSettingsApi.deleteSetting(id),
		// Awaited, so the confirmation closes once the grid no longer has the row
		onSuccess: async (result) => {
			if (result?.deleted) await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AppSettings.Settings] });
		},
	});
}
